import React, { useEffect, useRef } from "react";
import "./SimpleCaptcha.css";

export type CaptchaChallenge = {
  code: string;
  answer?: string;
  proof?: string;
  expiresAt?: number;
};

export const createCaptchaChallenge = (): CaptchaChallenge => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "";
  for (let i = 0; i < 5; i += 1) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return {
    code,
    answer: code,
  };
};

type SimpleCaptchaProps = {
  challenge: CaptchaChallenge;
  value: string;
  onChange: (value: string) => void;
  onRefresh: () => void;
  label: string;
  placeholder: string;
  refreshLabel: string;
  error?: string;
  idPrefix: string;
  layout?: "inline" | "stacked";
  hideLabel?: boolean;
};

const SimpleCaptcha: React.FC<SimpleCaptchaProps> = ({
  challenge,
  value,
  onChange,
  onRefresh,
  label,
  placeholder,
  refreshLabel,
  error,
  idPrefix,
  layout = "inline",
  hideLabel = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const seed = Math.random();

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#f2f6fb";
    ctx.fillRect(0, 0, width, height);

    // Noise lines
    for (let i = 0; i < 8; i += 1) {
      ctx.strokeStyle = `rgba(90, 110, 145, ${0.18 + Math.random() * 0.18})`;
      ctx.lineWidth = 1 + Math.random();
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Dots noise
    for (let i = 0; i < 140; i += 1) {
      const alpha = 0.07 + Math.random() * 0.2;
      ctx.fillStyle = `rgba(60, 76, 102, ${alpha})`;
      ctx.fillRect(Math.random() * width, Math.random() * height, 1.2, 1.2);
    }

    // Distorted captcha text
    const chars = challenge.code.split("");
    const baseX = 16;
    const gap = 24;
    chars.forEach((char, index) => {
      const x = baseX + index * gap + Math.sin(seed * 20 + index) * 2.5;
      const y = 31 + Math.cos(seed * 15 + index) * 4;
      const angle = (Math.random() - 0.5) * 0.55;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.font = `${20 + Math.floor(Math.random() * 4)}px "Trebuchet MS", "Arial", sans-serif`;
      ctx.fillStyle = `rgba(${25 + Math.floor(Math.random() * 70)}, ${35 + Math.floor(Math.random() * 70)}, ${70 + Math.floor(Math.random() * 90)}, 0.95)`;
      ctx.shadowColor = "rgba(90,110,145,0.35)";
      ctx.shadowBlur = 1.5;
      ctx.fillText(char, 0, 0);
      ctx.restore();
    });

    // Overlay haze to blur readability for bots
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "rgba(255,255,255,0.18)");
    gradient.addColorStop(0.5, "rgba(240,245,255,0.08)");
    gradient.addColorStop(1, "rgba(255,255,255,0.16)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }, [challenge.code]);

  return (
    <div className={`simple-captcha simple-captcha--${layout}`}>
      {!hideLabel ? (
        <label htmlFor={`${idPrefix}-captcha-input`} className="simple-captcha__label">
          {label}
        </label>
      ) : null}
      <div className="simple-captcha__row">
        <div className="simple-captcha__image-wrap">
          <canvas
            ref={canvasRef}
            width={160}
            height={44}
            className="simple-captcha__canvas"
            aria-label="Captcha image"
          />
        </div>
        <button
          type="button"
          className="simple-captcha__refresh"
          onClick={onRefresh}
          aria-label={refreshLabel}
          title={refreshLabel}
        >
          ↻
        </button>
        <input
          id={`${idPrefix}-captcha-input`}
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="simple-captcha__input"
          placeholder={placeholder}
          style={{ textTransform: "uppercase" }}
        />
      </div>
      {error ? <p className="simple-captcha__error">{error}</p> : null}
    </div>
  );
};

export default SimpleCaptcha;
